import { useEffect, useMemo, useState } from 'react';
import { orderService, Order } from '../services';

const PASSOS = [
  { status: 'PENDING', label: 'Recebido', desc: 'O pedido chegou na cozinha' },
  { status: 'CONFIRMED', label: 'Confirmado', desc: 'A cozinha aceitou o pedido' },
  { status: 'PREPARING', label: 'Em preparo', desc: 'Seus pratos estão sendo preparados' },
  { status: 'READY', label: 'Pronto!', desc: 'Seu pedido está pronto — o garçom já está levando' },
];

const STATUS_FINAL = ['PAID', 'CANCELLED', 'DELIVERED'];

const MENSAGEM_FINAL: Record<string, string> = {
  DELIVERED: 'Pedido entregue na mesa. Bom apetite! 🍽️',
  PAID: 'Conta fechada. Obrigado pela visita! 👋',
  CANCELLED: 'Este pedido foi cancelado.',
};

/** Tempo (ms) até a tela do recibo fechar sozinha após a conta ser fechada */
const TEMPO_RECIBO = 5 * 60 * 1000;

interface PedidoStatusProps {
  pedidoInicial: Order;
  onVoltar: () => void;
  onNovoPedido: () => void;
  /** Chamado ~5 min depois da conta fechada: a comanda some da mesa */
  onFecharResumo?: () => void;
}

/** Estado de cada item derivado do status da comanda (o sistema não marca por item) */
function estadoItem(status: string) {
  if (status === 'CANCELLED') return { texto: 'cancelado', classe: 'text-red-400' };
  if (status === 'PAID' || status === 'DELIVERED') return { texto: '✓ chegou', classe: 'text-green-400' };
  if (status === 'READY') return { texto: 'pronto', classe: 'text-green-300' };
  // PENDING/CONFIRMED/PREPARING: sem estado por item — os passos "Acompanhe"
  // abaixo já mostram em que etapa a comanda está como um todo.
  return null;
}

export default function PedidoStatus({
  pedidoInicial,
  onVoltar,
  onNovoPedido,
  onFecharResumo,
}: PedidoStatusProps) {
  const [pedido, setPedido] = useState<Order>(pedidoInicial);

  useEffect(() => {
    let ativo = true;
    const timer = setInterval(async () => {
      try {
        const r = await orderService.findById(pedidoInicial.id);
        if (!ativo) return;
        setPedido(r.data.data);
        if (STATUS_FINAL.includes(r.data.data.status)) {
          clearInterval(timer);
        }
      } catch {
        // mantém o último estado conhecido
      }
    }, 4000);
    return () => {
      ativo = false;
      clearInterval(timer);
    };
  }, [pedidoInicial.id]);

  // Conta fechada: mostra o recibo por ~5 minutos e depois some da mesa
  useEffect(() => {
    if (pedido.status !== 'PAID' || !onFecharResumo) return;
    const timer = setTimeout(onFecharResumo, TEMPO_RECIBO);
    return () => clearTimeout(timer);
  }, [pedido.status, onFecharResumo]);

  const indiceAtual = PASSOS.findIndex((p) => p.status === pedido.status);
  const finalizado = STATUS_FINAL.includes(pedido.status);
  // Mostra a comanda (itens) também quando entregue — só finaliza de vez em PAID/Cancelado
  const mostrarItens = pedido.status !== 'PAID' && pedido.status !== 'CANCELLED';
  const cancelado = pedido.status === 'CANCELLED';
  const contaFechada = pedido.status === 'PAID';

  // Agrupa os itens pelo número do pedido (lote): o 1º "Pedir Mais" vira 2, etc.
  const lotes = useMemo(() => {
    const m = new Map<number, Array<Order['items'][number]>>();
    for (const i of pedido.items) {
      const k = i.lote ?? 1;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(i);
    }
    return [...m.entries()].sort((a, b) => a[0] - b[0]);
  }, [pedido.items]);

  return (
    <div className="max-w-lg mx-auto">
      <div className="card p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-100">
              {contaFechada ? 'Conta fechada' : 'Seu Pedido'}
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              {pedido.table_number ? `Mesa ${pedido.table_number} · ` : ''}
              {pedido.customer_name}
            </p>
          </div>
          {!contaFechada && (
            <span className="text-xl font-bold text-primary-400">
              R$ {Number(pedido.total).toFixed(2)}
            </span>
          )}
        </div>

        {mostrarItens && (
          <div className="border-t pt-3 mb-2">
            <p className="text-sm font-semibold text-gray-300 mb-2">
              🧾 Sua comanda
            </p>
            <div className="space-y-3">
              {lotes.map(([lote, itens]) => (
                <div key={lote}>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Pedido {lote}
                  </p>
                  <div className="space-y-2">
                    {itens.map((item) => (
                      <div
                        key={item.id}
                        className={`flex justify-between text-sm ${item.cancelado ? 'opacity-50' : ''}`}
                      >
                        <span className="text-gray-200">
                          {item.quantity}x {item.product?.name}
                          {item.cancelado ? (
                            <span className="ml-1 text-xs text-red-400 bg-red-900/30 px-1.5 py-0.5 rounded">
                              ✕ cancelado
                            </span>
                          ) : (
                            (() => {
                              const e = estadoItem(pedido.status);
                              return e ? (
                                <span className={`ml-1 text-xs ${e.classe}`}>{e.texto}</span>
                              ) : null;
                            })()
                          )}
                          {item.lancado_por === 'garcom' && (
                            <span className="ml-1 text-xs text-purple-300 bg-purple-900/30 px-1.5 py-0.5 rounded">
                              🧑‍💼 garçom
                            </span>
                          )}
                          {item.notes && <span className="text-gray-400"> ({item.notes})</span>}
                        </span>
                        {!item.cancelado && (
                          <span className="text-gray-400">R$ {Number(item.subtotal).toFixed(2)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!finalizado ? (
          <>
            {/* Passos do pedido */}
            <div className="border-t pt-4 mb-6 mt-4">
              <p className="text-sm font-semibold text-gray-300 mb-2">Acompanhe:</p>
              <div className="space-y-3">
                {PASSOS.map((passo, i) => {
                  const concluido = indiceAtual > i;
                  const ativo = indiceAtual === i;
                  return (
                    <div
                      key={passo.status}
                      className={`flex items-start gap-3 p-3 rounded-lg border ${
                        ativo
                          ? 'border-primary-500/50 bg-primary-900/20'
                          : concluido
                            ? 'border-green-800/50 bg-green-900/10'
                            : 'border-gray-700 bg-gray-800/40'
                      }`}
                    >
                      <span
                        className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                          ativo
                            ? 'bg-primary-500 text-gray-950'
                            : concluido
                              ? 'bg-green-600 text-white'
                              : 'bg-gray-700 text-gray-400'
                        }`}
                      >
                        {concluido ? '✓' : i + 1}
                      </span>
                      <div>
                        <p className={`font-medium ${ativo ? 'text-primary-300' : concluido ? 'text-green-300' : 'text-gray-400'}`}>
                          {passo.label}
                        </p>
                        <p className="text-xs text-gray-400">{passo.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <button onClick={onVoltar} className="btn-primary flex-1">
                Ver Cardápio
              </button>
              <button onClick={onNovoPedido} className="btn-secondary flex-1">
                Pedir Mais
              </button>
            </div>
          </>
        ) : contaFechada ? (
          <>
            {/* Recibo da comanda fechada */}
            <div className="border-t pt-4">
              <div className="p-4 rounded-lg bg-green-900/20 text-green-300 mb-4 text-center">
                <p className="text-lg font-medium">✅ Conta fechada — obrigado pela visita!</p>
              </div>

              <div className="space-y-2 mb-4">
                {pedido.items.map((item) => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-gray-200">
                      {item.quantity}x {item.product?.name}
                      {item.lancado_por === 'garcom' && (
                        <span className="ml-1 text-xs text-purple-300 bg-purple-900/30 px-1.5 py-0.5 rounded">
                          🧑‍🍳 garçom
                        </span>
                      )}
                    </span>
                    <span className="text-gray-300">R$ {Number(item.subtotal).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-700 pt-3 mb-1 flex justify-between items-center">
                <span className="font-semibold text-gray-200">Total da comanda</span>
                <span className="text-3xl font-extrabold text-primary-400">
                  R$ {Number(pedido.total).toFixed(2)}
                </span>
              </div>
              <p className="text-xs text-gray-500 text-center mt-4">
                🕐 Esta tela fecha automaticamente em instantes
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 mt-4">
              <button onClick={onVoltar} className="btn-primary flex-1">
                Ver Cardápio
              </button>
              <button onClick={onNovoPedido} className="btn-secondary flex-1">
                Fazer Novo Pedido
              </button>
            </div>
          </>
        ) : (
          <>
            <div
              className={`p-4 rounded-lg mb-6 ${
                cancelado ? 'bg-red-900/20 text-red-300' : 'bg-green-900/20 text-green-300'
              }`}
            >
              <p className="font-medium">{MENSAGEM_FINAL[pedido.status] ?? MENSAGEM_FINAL.PAID}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button onClick={onVoltar} className="btn-primary flex-1">
                Ver Cardápio
              </button>
              <button onClick={onNovoPedido} className="btn-secondary flex-1">
                Fazer Novo Pedido
              </button>
            </div>
          </>
        )}
        <p className="text-xs text-gray-500 text-center mt-4">
          O status atualiza automaticamente
        </p>
      </div>
    </div>
  );
}